import { LocaleValue } from "@eres/core/dist/LocaleValue";
import { LocaleUtil, ServerDate } from "@eres/core";
import { LVStrings } from "@my-dld/assets/strings";
import { ValidationError } from "@10x/dubai-rest-api/dist/entities";

export enum PrvSellLookUp {
	PRVSell_COUNTRY = "Countries",
	TRANSACTION_DURATIONS = "TransactionDurations",
	PRVSell_DOCUMENT_TYPES = "DocumentTypes",
	PRVSell_GENDER = "Gender",
	PRVSell_PREFERRED_LANGUAGE = "PreferredLanguage",
	COMMENT_REASON = "CommentReason",
}

export const PrvSellLookUpStateKeys = {
	[ PrvSellLookUp.PRVSell_GENDER ]: "pvr_Gender",
	[ PrvSellLookUp.PRVSell_COUNTRY ]: "Countries",
	[ PrvSellLookUp.TRANSACTION_DURATIONS ]: "TransactionDurations",
	[ PrvSellLookUp.PRVSell_DOCUMENT_TYPES ]: "DocumentTypes",
	[ PrvSellLookUp.PRVSell_PREFERRED_LANGUAGE ]: "PreferredLanguage",
	[ PrvSellLookUp.COMMENT_REASON ]: "CommentReasons",
};
export const EmiratesID = {
	"Identity": 1,
	"Name": "emirates_id",
};
export const PassportID = {
	"Identity": 2,
	"Name": "passport_information",
};

export interface IDType extends LocaleUtil.LocaleValue {
	Id: number;
}

export const IDTypes: IDType[] = [];
IDTypes.push( {
	...LVStrings.emirates_id,
	Id: 1,
} );
IDTypes.push( {
	...LVStrings.passport,
	Id: 2,
} );

export const BookingInvoice = {
	Id: 1,
	...LVStrings.booking_amount,
};

export const FullPaymentInvoice = {
	Id: 2,
	...LVStrings.full_payment,
};

export interface ServiceBlock {
	isValid: boolean;
	errors: {
		isWarning: boolean;
		errorMessageSet: LocaleValue;
	}[];
}

export interface PvtSellLookUp {
	identity?: number;
	nameEn?: string;
	nameAr?: string;
	parentId?: any;
}


export interface PvtSellResponse {
	actions: number[];
	errors: Error[];
	additionalData: any;
	certificate: CertificateDetail;
	invoices: Invoice[];
	receipts: Receipt[];
	request: PvtSellRequest;
	termsAndConditions: Terms[];
}

export interface Error extends ValidationError {
	exceptionObject: string;
	items: { key: string, value: string }[];
	validationType: number;
}

export interface PvtSellRequest {
	id: number;
	propertyInfo: PropertyDetail;
	bookingRequestInfo: BookingRequestInfo;
	transactionType: number;
	sellRequestInfo: SellRequestInfo;
	termsAndConditions: Terms[];
}

export interface PSDocument {
	fileName?: string;
	fileSize?: number;
	localFile?: File;
	localData?: string;
	type?: string;
	identity?: number;
	documentType?: PvtSellLookUp;
	remoteDoc?: RemoteDocument;
}

export interface RemoteDocument {
	id: number;
	url: string;
	documentType: PvtSellLookUp;
	documentTypeId: number;
}

export interface uploadDocResponse {
	fileName: string;
	fileShortPath: string;
	exceptionMessage: string;
}

export interface SellRequestInfo {
	askingPrice?: string;
	transactionDuration?: PvtSellLookUp;
	documents?: RemoteDocument[];
	sellerDetails?: SellerDetails;
	description?: string;
	bankAccount?: BankAccount;
}

export interface BankAccount {
	accountTitle?: string;
	iban?: string;
	bankCountry?: PvtSellLookUp;
}

export interface NameSet {
	englishName?: string;
	arabicName?: string;
}

export interface SellerDetails {
	sellerId?: number;
	name?: NameSet;
	mobile?: string;
	email?: string;
	participantId?: number;
}

export interface BookingRequestInfo {
	referenceNumber?: string;
	documents?: PSDocument[];
	buyerInfo?: BuyerInfo;
	bookingRequest?: BookingRequest;
}

export interface BuyerInfo {
	id?: number;
	buyerId?: number;
	participantId?: number;
	birthDate?: ServerDate;
	name?: NameSet;
	birthPlace?: NameSet;
	documents?: PSDocument[];
	email?: string;
	mobile?: string;
	emiratesId?: string;
	emiratesIdExpiry?: ServerDate;
	nationality?: PvtSellLookUp;
	gender?: PvtSellLookUp;
	passportNumber?: string;
	passportIssueCountry?: PvtSellLookUp;
	passportExpiryDate?: ServerDate;
	passportIssueDate?: ServerDate;
	passportIssuePlace?: NameSet;
	preferredLanguage?: PvtSellLookUp;
}

export interface BookingRequest {
	bookingRequestId?: number;
	sellRequestId?: number;
	finalPrice?: number;
	bookingStartDate?: ServerDate;
	bookingExpiryDate?: ServerDate;
	appRequestId?: number;
	bookingReferenceNumber?: string;
	bookingStatusId?: number;
	bookingStatus?: PvtSellLookUp;
	commentList?: Comment[];
}

export interface Comment {
	commentId?: number;
	comment?: string;
	createdDate?: ServerDate;
	createdBy?: NameSet;
}

export interface PropertyDetail {
	propertyId: number;
	propertyType?: number;
	ownerParticipantId: number;
	propertyObject?: string;
}

export interface Terms {
	transactionTermsId: number;
	termId: number;
	description: NameSet;
	title: NameSet;
	isReadOnly: boolean;
}

export interface SellRequestListing {
	id: number;
	propertyInfo: PropertyDetail;
	transactionType: number;
	sellDetail?: SellRequestInfo;
	status?: PvtSellLookUp;
	actions?: PSellAction[];
}

export interface PSellAction {
	actionId: string;
	englishName?: string;
	arabicName?: string;
}

export interface Invoice {
	invoiceId: number;
	invoiceNumber: string;
	amount: number;
	tabVoucherNumber: number;
	tabVoucherYear: number;
	invoiceType: PvtSellLookUp;
	invoicePayee: PvtSellLookUp;
	invoiceStatus: PvtSellLookUp;
	invoiceDetails: InvoiceDetail[];
}

export interface InvoiceDetail {
	feeName: NameSet;
	feeCode: string;
	feeValue: number;
	amount: number;
}

export interface Receipt {
	id: number;
	receiptNumber: string;
	amount: number;
	paidOn: string;
	paymentMode: PvtSellLookUp;
	status: PvtSellLookUp;
	invoiceType: PvtSellLookUp;
	path: string;
}

export interface CertificateDetail {
	certificateNumber: number;
	certificateYear: number;
	filtPath: string;
}

export interface PaymentData {
	invoices: Invoice[];
	receipts: Receipt[];
	paymentUrl: string;
	totalAmountPayable: number;
}

// export interface ListingOption {
//     transactionTermsId: number;
//     termId: number;
//     description: NameSet;
//     title: NameSet;
//     isReadOnly: boolean;
// }

export function DocIdtoUploadType( id: number ) {
	switch ( id ) {
		case 0:
			return "None";
		case 2:
			return "Passport";
		case 1:
			return "EmiratesIdBack";
		case 3:
			return "TitleDeed";
		case 4:
			return "PropertyImages";
		case 5:
			return "PowerOfAttorney";
		case 6:
			return "Others";
		case 7:
			return "PaymentReceipt";
		case 8:
			return "EmiratesIdFront";
		default:
			return "None";
	}
}

export enum PSellRequestStatusId {
	Booked = 2,
	Submit_To_Audit,
	Approved,
	Completed = 6,
	Booking_Expired,
	Buyer_Reject = 12,
	Audit_Rejected = 13,
}

export enum PSRole {
	BUYER,
	SELLER
}

export interface ITRAction {
	id: number;
	positive: boolean;
	user: "buyer" | "seller" | "both";
	label: LocaleUtil.LocaleValue;
}

export const AllTRAction: ITRAction[] = [
	{
		id: 9,
		positive: false,
		user: "seller",
		label: LVStrings.cancel_request,
	},
	{
		id: 1,
		positive: true,
		user: "seller",
		label: LVStrings.review_request,
	},

	{
		id: 3,
		positive: false,
		user: "buyer",
		label: LVStrings.reject,
	},
	{
		id: 2,
		positive: true,
		user: "buyer",
		label: LVStrings.accept,
	},
	{
		id: 4,
		positive: true,
		user: "buyer",
		label: LVStrings.property_proceed_to_payment,
	},
	{
		id: 5,
		positive: true,
		user: "buyer",
		label: LVStrings.property_proceed_to_payment,
	},
	{
		id: 8,
		positive: true,
		user: "both",
		label: LVStrings.download_receipt,
	},
	{
		id: 10,
		positive: true,
		user: "both",
		label: LVStrings.download_certificate,
	},
];
